import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Trash2, Upload } from "lucide-react";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass } from "../components/FormField";
import {
  createHospitalDiagnosis,
  deleteHospitalDiagnosis,
  fetchHospitalDiagnoses,
  importHospitalDiagnosesCsv,
  updateHospitalDiagnosis,
  type HospitalDiagnosis,
} from "../api/hospitalApi";
import { getAuthToken } from "../auth";

export function HospitalDiagnosesPage() {
  const { showToast } = usePageActions();
  const [rows, setRows] = useState<HospitalDiagnosis[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);

  const load = useCallback(async () => {
    if (!getAuthToken()) return;
    setLoading(true);
    try {
      const res = await fetchHospitalDiagnoses({ q: search || undefined });
      setRows(res.diagnoses ?? []);
    } catch (e) {
      showToast({
        message: e instanceof ApiError ? e.message : "Chargement impossible.",
        variant: "warning",
      });
    } finally {
      setLoading(false);
    }
  }, [search, showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => rows, [rows]);

  const resetForm = () => {
    setEditingId(null);
    setCode("");
    setName("");
  };

  const handleSave = async () => {
    if (!code.trim() || !name.trim()) {
      showToast({ message: "Code et libellé obligatoires.", variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateHospitalDiagnosis(editingId, { code: code.trim(), name: name.trim() });
        showToast({ message: "Diagnostic mis à jour.", variant: "success" });
      } else {
        await createHospitalDiagnosis({ code: code.trim(), name: name.trim() });
        showToast({ message: "Diagnostic créé.", variant: "success" });
      }
      resetForm();
      await load();
    } catch (e) {
      showToast({
        message: e instanceof ApiError ? e.message : "Échec de l'enregistrement.",
        variant: "warning",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row: HospitalDiagnosis) => {
    setEditingId(row.id);
    setCode(row.code);
    setName(row.name);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Supprimer ce diagnostic ?")) return;
    try {
      await deleteHospitalDiagnosis(id);
      showToast({ message: "Diagnostic supprimé.", variant: "success" });
      await load();
    } catch (e) {
      showToast({
        message: e instanceof ApiError ? e.message : "Suppression impossible.",
        variant: "warning",
      });
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      showToast({ message: "Choisissez un fichier CSV.", variant: "warning" });
      return;
    }
    try {
      const result = await importHospitalDiagnosesCsv(importFile);
      showToast({
        message: `Import : ${result.imported} créés, ${result.duplicates} doublons.`,
        variant: "success",
      });
      setImportFile(null);
      await load();
    } catch (e) {
      showToast({
        message: e instanceof ApiError ? e.message : "Import impossible.",
        variant: "warning",
      });
    }
  };

  return (
    <>
      <Breadcrumbs
        homeHref="/dashboard/hospital"
        items={[{ label: "Configuration" }, { label: "Diagnostics établissement" }]}
      />
      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground">Diagnostics de l&apos;établissement</h1>
        <p className="mt-1 text-sm text-muted">
          Catalogue local des diagnostics (table hospital_diagnoses), distinct du référentiel standard.
        </p>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="theme-card space-y-3 p-5">
          <h2 className="text-sm font-semibold">{editingId ? "Modifier" : "Nouveau diagnostic"}</h2>
          <FormField label="Code">
            <input className={inputClass} value={code} onChange={(e) => setCode(e.target.value)} />
          </FormField>
          <FormField label="Libellé">
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
          </FormField>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => void handleSave()}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
            >
              <Plus size={14} />
              {editingId ? "Enregistrer" : "Ajouter"}
            </button>
            {editingId && (
              <button type="button" onClick={resetForm} className="rounded-lg border border-border px-3 py-2 text-sm">
                Annuler
              </button>
            )}
          </div>
        </div>

        <div className="theme-card space-y-3 p-5">
          <h2 className="text-sm font-semibold">Import CSV</h2>
          <p className="text-xs text-muted">Colonnes : code, name (en-tête optionnel)</p>
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            onClick={() => void handleImport()}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm"
          >
            <Upload size={14} />
            Importer
          </button>
        </div>
      </div>

      <div className="theme-card p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <input
            className={inputClass + " max-w-xs"}
            placeholder="Rechercher…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {loading && <span className="text-sm text-muted">Chargement…</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-muted">
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Libellé</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id} className="border-b border-border last:border-0">
                  <td className="px-3 py-2 text-sm font-medium">{row.code}</td>
                  <td className="px-3 py-2 text-sm">{row.name}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2">
                      <button type="button" onClick={() => handleEdit(row)} className="text-primary">
                        <Pencil size={14} />
                      </button>
                      <button type="button" onClick={() => void handleDelete(row.id)} className="text-pink">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan={3} className="px-3 py-8 text-center text-sm text-muted">
                    Aucun diagnostic.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
